import { useMemo } from 'react';
import { useLocale } from '@hooks/useLocale';
import { getBreadcrumbHrefs } from '@utils/algolia/getBreadcrumbHrefs';
import Breadcrumbs, { BreadcrumbItem } from './index';
import BreadcrumbsJsonLd from './BreadcrumbsJsonLd';

export type CategoryBreadcrumbsProps = {
  /**
   * Ordered category names, e.g. `['Products', "Men's", 'Clothing']`. On the PLP
   * this is the Algolia `hierarchicalMenu['categories.lvl0']` state, so the trail
   * updates as facets are refined.
   */
  breadcrumbCategories?: string[];
  className?: string;
  productName?: string;
};

/**
 * Category-driven breadcrumbs for the redesigned PLP: turns category names into
 * hrefs with `getBreadcrumbHrefs` (shared with the PDP), renders the redesigned
 * `_components/Breadcrumbs`, and emits the `BreadcrumbList` structured data that
 * the component itself does not.
 */
const CategoryBreadcrumbs = ({
  breadcrumbCategories = [],
  className,
  productName,
}: CategoryBreadcrumbsProps) => {
  const locale = useLocale();

  const items = useMemo<BreadcrumbItem[]>(
    () =>
      getBreadcrumbHrefs(
        breadcrumbCategories.map((name) => ({ name })),
        locale
      ).map((crumb) => ({
        id: crumb.entityId?.toString() ?? crumb.name,
        label: crumb.name,
        href: crumb.path,
      })),
    [breadcrumbCategories, locale]
  );

  return (
    <>
      <BreadcrumbsJsonLd items={items} />
      <Breadcrumbs
        items={items}
        className={className}
        productName={productName}
      />
    </>
  );
};

export default CategoryBreadcrumbs;
